import React, { useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Helmet } from 'react-helmet';
import { ThemeContext } from '../contexts/ThemeContext';
import { LanguageContext } from '../contexts/LanguageContext';

const SustainabilityInitiative = ({ title, description, icon, color, delay = 0 }) => {
  const { theme } = useContext(ThemeContext);
  return (
    <div 
      className={`bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-t-4 ${color} overflow-hidden relative`}
      data-aos="fade-up"
      data-aos-delay={delay}
    >
      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-green-500/10 to-teal-500/20 rounded-full -mr-8 -mt-8"></div>
      <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-blue-500/10 to-green-500/20 rounded-full -ml-4 -mb-4"></div>
      
      <div className="flex items-start relative z-10">
        <div className="mr-4 text-4xl bg-gradient-to-br from-green-500 to-teal-500 w-12 h-12 flex items-center justify-center rounded-lg text-white">{icon}</div>
        <div>
          <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">{title}</h3>
          <p className="text-gray-700 dark:text-gray-300">{description}</p>
        </div>
      </div>
    </div>
  );
};

// Commented out unused component to fix ESLint errors
/* 
const SustainabilityGoal = ({ number, title, description, image, color, delay = 0 }) => {
  return (
    <div 
      className="flex flex-col items-center p-8 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden relative group"
      data-aos="zoom-in"
      data-aos-delay={delay}
    >
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 to-teal-500 transform origin-left transition-transform duration-300 group-hover:scale-x-100"></div>
      <div className={`w-20 h-20 flex items-center justify-center rounded-full ${color} text-white font-bold text-2xl mb-6 transform transition-transform duration-500 group-hover:scale-110 shadow-lg`}>
        {number}
      </div>
      <h3 className="text-xl font-bold text-gray-800 mb-3 text-center group-hover:text-green-600 transition-colors duration-300">{title}</h3>
      <p className="text-gray-700 text-center">{description}</p>
    </div>
  );
};
*/


const Sustainability = () => {
  const { theme } = useContext(ThemeContext);
  const { translations } = useContext(LanguageContext);

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      mirror: false,
      easing: 'ease-out-cubic'
    });
    
    window.scrollTo(0, 0);
    
    return () => {
      AOS.refresh();
    };
  }, []);
  const initiatives = [
    {
      title: translations['sustainability.initiatives.green.title'],
      description: translations['sustainability.initiatives.green.description'],
      icon: "🌱",
      color: "border-green-500"
    },
    {
      title: translations['sustainability.initiatives.remote.title'],
      description: translations['sustainability.initiatives.remote.description'],
      icon: "🏡",
      color: "border-blue-500"
    },
    {
      title: translations['sustainability.initiatives.development.title'],
      description: translations['sustainability.initiatives.development.description'],
      icon: "♻️",
      color: "border-teal-500"
    },
    {
      title: translations['sustainability.initiatives.cloud.title'],
      description: translations['sustainability.initiatives.cloud.description'],
      icon: "☁️",
      color: "border-purple-500"
    }
  ];

  // Removed unused goals array to fix ESLint errors

  return (
    <div className="py-20 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950 min-h-screen">
      <Helmet>
        <title>{translations['sustainability.pageTitle']}</title>
        <meta name="description" content={translations['sustainability.meta.description']} />
      </Helmet>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16" data-aos="fade-up">
            <h1 className="text-5xl md:text-6xl font-extrabold text-gray-800 mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-green-500 to-teal-500">{translations['sustainability.title'] || 'Sustainability Commitment'}</span>
            </h1>
            
            <p className="text-black dark:text-white text-lg max-w-3xl mx-auto leading-relaxed mb-16 font-medium">
              {translations['sustainability.subtitle'] || 'At Fepsec, we\'re committed to promoting sustainable development by aligning our efforts with the UN\'s Sustainable Development Goals 9 and 13, which has become a core value in everything we do.'}
            </p>
            
            <div className="flex justify-center space-x-6 mb-8">
              <div className="flex items-center">
                <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                <span className="text-black dark:text-white font-medium">{translations['sustainability.planet']}</span>
              </div>
              <div className="flex items-center">
                <span className="w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
                <span className="text-black dark:text-white font-medium">{translations['sustainability.people']}</span>
              </div>
              <div className="flex items-center">
                <span className="w-3 h-3 bg-teal-500 rounded-full mr-2"></span>
                <span className="text-black dark:text-white font-medium">{translations['sustainability.progress']}</span>
              </div>
            </div>
          </div>
          
          {/* Introduction Card */}
          <div className="bg-gradient-to-r from-green-500 to-teal-500 p-1 rounded-2xl shadow-lg mb-16 overflow-hidden" data-aos="fade-up" data-aos-delay="100">
            <div className="bg-white dark:bg-gray-900 p-8 md:p-10 rounded-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-green-500/20 to-teal-500/20 rounded-full -mr-16 -mt-16"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-green-500/20 to-blue-500/20 rounded-full -ml-10 -mb-10"></div>
              
              <div className="relative z-10">
                <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-6 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {translations['sustainability.technologyPurpose']}
                </h2>
                
                <p className="text-black dark:text-white text-lg leading-relaxed mb-6">
                  {translations['sustainability.introText1']}
                </p>
                
                <p className="text-black dark:text-white text-lg leading-relaxed">
                  {translations['sustainability.introText2']}
                </p>
              </div>
            </div>
          </div>

          <div className="mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-8 flex items-center" data-aos="fade-up">
              <span className="inline-block mr-3 bg-green-500 rounded-full p-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </span>
              {translations['sustainability.focusAreas']}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              {initiatives.map((initiative, index) => (
                <SustainabilityInitiative
                  key={index}
                  title={initiative.title}
                  description={initiative.description}
                  icon={initiative.icon}
                  color={initiative.color}
                  delay={index * 100}
                />
              ))}
            </div>
            
            <div className="mb-16 relative z-10" data-aos="fade-up">
              <div className="text-center mb-10">
                <h3 className="text-3xl font-bold mb-4 inline-block relative">
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-teal-500 to-green-600">UN Sustainable Development Goals</span>
                  <div className="absolute -bottom-2 left-0 right-0 h-1 rounded-full bg-gradient-to-r from-blue-600 via-teal-500 to-green-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                </h3>
                <p className="text-gray-700 dark:text-gray-300 max-w-3xl mx-auto text-lg">
                  We align our sustainability efforts with the UN's Sustainable Development Goals, focusing particularly on:
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                <div className="group bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden transform transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl relative" data-aos="fade-right">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  <div className="p-1 bg-gradient-to-r from-blue-500 to-indigo-600">
                    <div className="bg-white dark:bg-gray-900 p-8 rounded-t-xl relative z-10 group-hover:bg-white/5 dark:group-hover:bg-gray-900/5 transition-colors duration-300">
                      <div className="flex items-center mb-6">
                        <div className="flex-shrink-0 mr-4">
                          <img src="/images/sdg-09.png" alt="SDG 9" className="h-16 w-16 rounded shadow-md" />
                        </div>
                        <div>
                          <span className="text-sm font-bold text-blue-600 uppercase tracking-wider group-hover:text-white transition-colors duration-300">SDG 9</span>
                          <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 group-hover:text-white transition-colors duration-300">{translations['sustainability.goal9.title']}</h4>
                        </div>
                      </div>
                      
                      <p className="text-gray-700 dark:text-gray-300 group-hover:text-white/90 transition-colors duration-300">
                        {translations['sustainability.goal9.description']}
                      </p>
                    </div>
                  </div>
                  
                  <div className="p-8 bg-white dark:bg-gray-900 group-hover:bg-blue-600 transition-colors duration-300">
                    <h5 className="font-bold text-gray-800 dark:text-gray-100 mb-3 group-hover:text-white transition-colors duration-300">{translations['sustainability.ourContribution']}</h5>
                    <ul className="space-y-2">
                      {[
                        translations['sustainability.goal9.contribution1'],
                        translations['sustainability.goal9.contribution2'],
                        translations['sustainability.goal9.contribution3'],
                        translations['sustainability.goal9.contribution4']
                      ].map((item, index) => (
                        <li key={index} className="flex items-start">
                          <svg className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0 group-hover:text-white transition-colors duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-gray-700 dark:text-gray-300 group-hover:text-white/90 transition-colors duration-300">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <div className="group bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden transform transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl relative" data-aos="fade-left">
                  <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-teal-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  <div className="p-1 bg-gradient-to-r from-green-500 to-teal-600">
                    <div className="bg-white dark:bg-gray-900 p-8 rounded-t-xl relative z-10 group-hover:bg-white/5 dark:group-hover:bg-gray-900/5 transition-colors duration-300">
                      <div className="flex items-center mb-6">
                        <div className="flex-shrink-0 mr-4">
                          <img src="/images/sdg-13.png" alt="SDG 13" className="h-16 w-16 rounded shadow-md" />
                        </div>
                        <div>
                          <span className="text-sm font-bold text-green-600 uppercase tracking-wider group-hover:text-white transition-colors duration-300">SDG 13</span>
                        </div>
                      </div>
                      
                      <p className="text-gray-700 dark:text-gray-300 group-hover:text-white/90 transition-colors duration-300">
                        {translations['sustainability.goal13.description']}
                      </p>
                    </div>
                  </div>
                  
                  <div className="p-8 bg-white dark:bg-gray-900 group-hover:bg-green-600 transition-colors duration-300">
                    <h5 className="font-bold text-gray-800 dark:text-gray-100 mb-3 group-hover:text-white transition-colors duration-300">{translations['sustainability.ourContribution']}</h5>
                    <ul className="space-y-2">
                      {[
                        translations['sustainability.goal13.contribution1'],
                        translations['sustainability.goal13.contribution2'],
                        translations['sustainability.goal13.contribution3'],
                        translations['sustainability.goal13.contribution4']
                      ].map((item, index) => (
                        <li key={index} className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0 group-hover:text-white transition-colors duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-gray-700 dark:text-gray-300 group-hover:text-white/90 transition-colors duration-300">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="text-center mt-8" data-aos="fade-up" data-aos-delay="200">
                <a 
                  href="https://sdgs.un.org/goals" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-teal-600 hover:text-teal-700 font-semibold transition-colors duration-300"
                >
                  <span>{translations['sustainability.learnMore']}</span>
                  <svg className="ml-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </a>
              </div>
            </div>
          </div>



          <div className="mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-8 flex items-center" data-aos="fade-up">
              <span className="inline-block mr-3 bg-teal-500 rounded-full p-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </span>
              {translations['sustainability.initiatives.title']}
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              <div className="bg-white dark:bg-gray-900 p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group" data-aos="fade-up" data-aos-delay="100">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-teal-500/10 to-green-500/20 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform duration-500"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center mb-4">
                    <div className="bg-gradient-to-r from-teal-500 to-green-500 rounded-lg p-2 mr-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 group-hover:text-teal-600 transition-colors duration-300">{translations['sustainability.green.title']}</h3>
                  </div>
                  
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {translations['sustainability.green.description']}
                  </p>
                  
                  <ul className="space-y-3">
                    {[
                      translations['sustainability.green.contribution1'],
                      translations['sustainability.green.contribution2'],
                      translations['sustainability.green.contribution3'],
                      translations['sustainability.green.contribution4']
                    ].map((item, index) => (
                      <li key={index} className="flex items-start">
                        <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700 dark:text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <div className="bg-white dark:bg-gray-900 p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group" data-aos="fade-up" data-aos-delay="200">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-teal-500/20 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform duration-500"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center mb-4">
                    <div className="bg-gradient-to-r from-blue-500 to-teal-500 rounded-lg p-2 mr-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 group-hover:text-blue-600 transition-colors duration-300">{translations['sustainability.initiatives.cloud.title']}</h3>
                  </div>
                  
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {translations['sustainability.initiatives.cloud.description']}
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                    <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg border-l-4 border-blue-500 hover:bg-blue-100 transition-colors duration-300">
                      <h4 className="font-bold text-gray-800 dark:text-gray-100 mb-2">{translations['sustainability.initiatives.cloud.contribution1.title']}</h4>
                      <p className="text-gray-700 dark:text-gray-300">{translations['sustainability.initiatives.cloud.contribution1.description']}</p>
                    </div>
                    <div className="bg-teal-50 dark:bg-teal-900/30 p-4 rounded-lg border-l-4 border-teal-500 hover:bg-teal-100 transition-colors duration-300">
                      <h4 className="font-bold text-gray-800 dark:text-gray-100 mb-2">{translations['sustainability.initiatives.cloud.contribution2.title']}</h4>
                      <p className="text-gray-700 dark:text-gray-300">{translations['sustainability.initiatives.cloud.contribution2.description']}</p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg border-l-4 border-green-500 hover:bg-green-100 transition-colors duration-300">
                      <h4 className="font-bold text-gray-800 dark:text-gray-100 mb-2">{translations['sustainability.initiatives.cloud.contribution3.title']}</h4>
                      <p className="text-gray-700 dark:text-gray-300">{translations['sustainability.initiatives.cloud.contribution3.description']}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-1 rounded-2xl shadow-xl overflow-hidden" data-aos="fade-up" data-aos-delay="300">
              <div className="bg-white dark:bg-gray-900 p-8 rounded-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-indigo-500/10 to-blue-500/10 rounded-full -mr-16 -mt-16"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center mb-6">
                    <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg p-2 mr-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">{translations['sustainability.commitment.education.title']}</h3>
                  </div>
                  
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                    {translations['sustainability.commitment.education.description']}
                  </p>
                  
                  <div className="bg-indigo-50 dark:bg-indigo-900/30 border-l-4 border-indigo-600 pl-6 py-4 rounded-r-lg">
                    <p className="text-gray-700 dark:text-gray-300 italic">
                      {translations['sustainability.commitment.education.quote']}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-green-500 to-teal-500 p-1 rounded-2xl shadow-xl mb-16 overflow-hidden" data-aos="fade-up">
            <div className="bg-white dark:bg-gray-900 p-8 md:p-10 rounded-2xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-tr from-green-500/10 to-teal-500/20 rounded-full -ml-10 -mt-10"></div>
              <div className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-to-br from-green-500/10 to-teal-500/20 rounded-full -mr-12 -mb-12"></div>
              
              <div className="relative z-10">
                <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-6 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-green-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  {translations['sustainability.green.title']}
                </h2>
                
                <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed mb-8">
                  {translations['sustainability.green.progress']}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div data-aos="fade-left" data-aos-delay="100" className="bg-green-50 dark:bg-green-900/30 p-6 rounded-xl border-l-4 border-green-500 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                    <div className="flex items-center mb-3">
                      <div className="bg-green-100 p-2 rounded-full mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <h3 className="font-bold text-gray-800 dark:text-gray-100">{translations['sustainability.commitment.measurement.title']}</h3>
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 ml-10">{translations['sustainability.commitment.measurement.description']}</p>
                  </div>
                  
                  <div data-aos="fade-right" className="bg-blue-50 dark:bg-blue-900/30 p-6 rounded-xl border-l-4 border-blue-500 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                    <div className="flex items-center mb-3">
                      <div className="bg-blue-100 p-2 rounded-full mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                      </div>
                      <h3 className="font-bold text-gray-800 dark:text-gray-100">{translations['sustainability.commitment.green.title']}</h3>
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 ml-10">{translations['sustainability.commitment.green.description']}</p>
                  </div>
                  
                  <div data-aos="fade-right" data-aos-delay="300" className="bg-teal-50 dark:bg-teal-900/30 p-6 rounded-xl border-l-4 border-teal-500 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                    <div className="flex items-center mb-3">
                      <div className="bg-teal-100 p-2 rounded-full mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                        </svg>
                      </div>
                      <h3 className="font-bold text-gray-800 dark:text-gray-100">{translations['sustainability.commitment.opensource.title']}</h3>
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 ml-10">{translations['sustainability.commitment.opensource.description']}</p>
                  </div>
                  
                  <div data-aos="fade-left" data-aos-delay="400" className="bg-indigo-50 dark:bg-indigo-900/30 p-6 rounded-xl border-l-4 border-indigo-500 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                    <div className="flex items-center mb-3">
                      <div className="bg-indigo-100 p-2 rounded-full mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                        </svg>
                      </div>
                      <h3 className="font-bold text-gray-800 dark:text-gray-100">{translations['sustainability.commitment.partnerships.title']}</h3>
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 ml-10">{translations['sustainability.commitment.partnerships.description']}</p>
                  </div>
                </div>
                
                <p className="text-black dark:text-white text-lg leading-relaxed">
                  {translations['sustainability.commitment.progress']}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-2xl p-12 text-center mb-8 shadow-xl relative overflow-hidden" data-aos="fade-up">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-2xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full -ml-32 -mb-32 blur-2xl"></div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 relative z-10">{translations['sustainability.join.title']}</h2>
            <p className="text-white text-opacity-90 max-w-2xl mx-auto mb-10 text-lg relative z-10">
              {translations['sustainability.join.description']}
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4 relative z-10">
              <Link 
                to="/contact-us" 
                className="inline-block bg-white text-green-600 hover:bg-gray-100 font-bold px-8 py-4 rounded-lg transition transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                {translations['sustainability.partnerCta']}
              </Link>
              <Link 
                to="/open-source" 
                className="inline-block bg-transparent text-white border-2 border-white hover:bg-white/10 font-bold px-8 py-4 rounded-lg transition transform hover:scale-105"
              >
                {translations['nav.openSource'] || 'Explore Our Open Source Work'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sustainability;
